import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Loader2, LogOut, MapPin, Package, Scissors } from "lucide-react";
import AddressBook from "../components/store/AddressBook";
import BuyAgain from "../components/store/BuyAgain";
import { StarInput } from "../components/store/Stars";
import { rateProduct } from "../components/store/ProductRatings";
import { toast } from "sonner";

const BOUGHT = ["paid", "packed", "shipped", "delivered"];
import { STORE_API, useStoreBrands } from "../store/CartContext";
import { BrandTile } from "../components/SiteHeader";
import { useAccount } from "../store/AccountContext";
import ProductCard from "../components/store/ProductCard";
import { OrderView } from "./TrackOrder";
import { ProductRail } from "./Shop";
import { BREEDS, FEATURED_BRANDS, LIFE_STAGES } from "../data/shopMenu";

const Page = ({ children, testid }) => <div data-testid={testid} className="min-h-screen bg-[#F7F5FF] text-[#1E0A5C]">{children}</div>;

export function BrandsPage() {
  const live = useStoreBrands() || [];
  const brands = [...new Set([...live, ...FEATURED_BRANDS])];
  return (
    <Page testid="brands-page">
      <section className="container-custom py-10">
        <h1 className="font-heading text-3xl font-bold sm:text-4xl">All brands</h1>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {brands.map((b) => <BrandTile key={b} b={b} live={live.includes(b)} className="h-24 !bg-white !text-base" />)}
        </div>
      </section>
    </Page>
  );
}

export function BreedsPage() {
  return (
    <Page testid="breeds-page">
      <section className="container-custom py-10">
        <h1 className="font-heading text-3xl font-bold sm:text-4xl">Shop by breed</h1>
        <p className="mt-1 text-sm text-[#5E5675]">Nutrition, grooming kits and bundles picked for your pet's breed.</p>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {BREEDS.map((b) => (
            <Link key={b.slug} to={`/breeds/${b.slug}`} data-testid={`breeds-card-${b.slug}`} className="group overflow-hidden rounded-3xl bg-white">
              <img src={b.img} alt={b.name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="p-4"><p className="font-heading font-bold">{b.name}</p><p className="text-xs text-[#5E5675]">{b.pet} · {b.size} · {b.coat}</p></div>
            </Link>
          ))}
        </div>
      </section>
    </Page>
  );
}

export function BreedPage() {
  const { slug } = useParams();
  const b = BREEDS.find((x) => x.slug === slug);
  const [stage, setStage] = useState("Adult");
  useEffect(() => setStage("Adult"), [slug]);
  if (!b) return <Page><p className="py-24 text-center">Breed not found. <Link to="/breeds" className="font-bold text-[#FC5106]">See all breeds</Link></p></Page>;
  return (
    <Page testid="breed-page">
      <section className="bg-[#3915AA]">
        <div className="container-custom grid items-center gap-6 py-10 md:grid-cols-[1fr_340px]">
          <div>
            <p className="text-xs text-white/60"><Link to="/breeds" className="hover:text-white">Breeds</Link> / {b.name}</p>
            <h1 className="mt-2 font-heading text-3xl font-bold text-white sm:text-5xl">{b.name}</h1>
            <p className="mt-2 text-sm text-white/70">{b.pet} · {b.size} breed · {b.coat}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-white/10 p-4 text-sm text-white"><p className="mb-1 font-bold text-[#FFB08A]">Nutrition tip</p>{b.food}</div>
              <div className="rounded-2xl bg-white/10 p-4 text-sm text-white"><p className="mb-1 font-bold text-[#FFB08A]">Grooming tip</p>{b.groom}</div>
            </div>
            <Link to="/contact" data-testid="breed-book-grooming" className="btn-primary mt-5 !py-3"><Scissors className="h-4 w-4" /> Book a {b.name} groom</Link>
          </div>
          <img src={b.img} alt={b.name} className="aspect-square w-full rounded-3xl object-cover shadow-2xl" />
        </div>
      </section>
      <ProductRail title={`Food for ${b.size.toLowerCase()} ${b.pet === "Dog" ? "dogs" : "cats"}`} params={{ pet: b.pet, category: "food" }} testid="breed-food" link={`/shop/c/food?pet=${b.pet}`} />
      <ProductRail title={`Grooming kit for ${b.coat.toLowerCase()}s`} params={{ pet: b.pet, category: "grooming" }} testid="breed-grooming" link={`/shop/c/grooming?pet=${b.pet}`} />
      <section className="container-custom pt-6">
        <h2 className="font-heading text-xl font-bold sm:text-3xl">Bundles by age</h2>
        <div className="mt-3 flex gap-2">{LIFE_STAGES[b.pet].map((s) => <button key={s} onClick={() => setStage(s)} data-testid={`breed-stage-${s.toLowerCase()}`} className={`rounded-full px-4 py-2 text-sm font-bold ${stage === s ? "bg-[#3915AA] text-white" : "bg-white text-[#1E0A5C]"}`}>{s}</button>)}</div>
      </section>
      <ProductRail title={`${stage} picks for your ${b.name}`} params={{ pet: b.pet, life_stage: stage }} testid="breed-stage-rail" />
      <div className="pb-10" />
    </Page>
  );
}

export default function AccountPage() {
  const { user, login, logout, wishlist } = useAccount();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState(null);
  const [wish, setWish] = useState(null);
  const { api } = useAccount();

  useEffect(() => { if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, [user, api]);
  const [myRatings, setMyRatings] = useState({});
  const [rateable, setRateable] = useState([]);
  useEffect(() => { if (user) api.get("/reviews").then((r) => { setMyRatings(r.data.ratings); setRateable(r.data.rateable); }).catch(() => {}); }, [user, api]);
  const rate = async (pid, n) => {
    try { await rateProduct(pid, n); setMyRatings((m) => ({ ...m, [pid]: n })); toast.success("Thanks for rating!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save your rating"); }
  };
  useEffect(() => {
    if (!wishlist.length) { setWish([]); return; }
    axios.get(`${STORE_API}/products`, { params: { ids: wishlist.join(","), per_page: 60 } }).then((r) => setWish(r.data.items)).catch(() => setWish([]));
  }, [wishlist]);

  if (user === undefined) return <Page><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#FC5106]" /></div></Page>;

  return (
    <Page testid="account-page">
      <section className="container-custom max-w-5xl py-10">
        {!user ? (
          <div data-testid="account-signin" className="mx-auto max-w-md rounded-3xl bg-white p-8 text-center">
            <h1 className="font-heading text-2xl font-bold">Sign in to BarknFurs</h1>
            <p className="mt-2 text-sm text-[#5E5675]">Track orders, save your wishlist across devices and check out faster.</p>
            <button onClick={() => login(`/account${tab === "wishlist" ? "?tab=wishlist" : ""}`)} data-testid="account-google-login" className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full border border-[#3915AA]/20 bg-white py-3 text-sm font-bold hover:bg-[#F7F5FF]">
              <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continue with Google
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-white p-5">
            <div className="flex items-center gap-3">
              {user.picture && <img src={user.picture} alt="" className="h-12 w-12 rounded-full" />}
              <div><p data-testid="account-name" className="font-heading text-lg font-bold">{user.name}</p><p className="text-xs text-[#5E5675]">{user.email}</p></div>
            </div>
            <button onClick={logout} data-testid="account-logout" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#5E5675] hover:text-red-500"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          {[["orders", "My orders", Package], ["wishlist", `Wishlist (${wishlist.length})`, Heart], ...(user ? [["addresses", "Addresses", MapPin]] : [])].map(([k, l, I]) => (
            <button key={k} onClick={() => setParams({ tab: k })} data-testid={`account-tab-${k}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${tab === k ? "bg-[#3915AA] text-white" : "bg-white"}`}><I className="h-4 w-4" />{l}</button>
          ))}
        </div>
        <div className="mt-5">
          {tab === "addresses" && user ? <AddressBook /> : tab === "wishlist" ? (
            !wish ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#FC5106]" /> : wish.length === 0 ? <p data-testid="wishlist-empty" className="rounded-3xl bg-white py-16 text-center text-sm text-[#5E5675]">No saved items yet. Tap the heart on any product.</p> :
              <div data-testid="wishlist-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{wish.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          ) : !user ? (
            <p className="rounded-3xl bg-white py-12 text-center text-sm text-[#5E5675]">Sign in to see your orders, or <Link to="/track-order" className="font-bold text-[#FC5106]">track an order</Link> with your order number.</p>
          ) : !orders ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#FC5106]" /> : orders.length === 0 ? <p data-testid="orders-empty" className="rounded-3xl bg-white py-12 text-center text-sm text-[#5E5675]">No orders yet. <Link to="/shop/all" className="font-bold text-[#FC5106]">Start shopping</Link></p> :
            <div className="space-y-4">{orders.map((o) => (
              <OrderView key={o.id} order={o}
                actions={o.status !== "cancelled" && <BuyAgain order={o} />}
                itemExtra={BOUGHT.includes(o.status) ? (i) => rateable.includes(i.product_id) && (
                  <div className="mt-1 flex items-center gap-2"><span className="text-[11px] text-[#5E5675]">{myRatings[i.product_id] ? "Your rating" : "Rate it"}</span>
                    <StarInput size="h-4 w-4" value={myRatings[i.product_id]} onChange={(n) => rate(i.product_id, n)} testid={`order-rate-${o.order_number}-${i.product_id.slice(0, 8)}`} /></div>
                ) : undefined} />
            ))}</div>}
        </div>
      </section>
    </Page>
  );
}

export function AccountCallback() {
  const { exchange } = useAccount();
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const to = window.location.pathname + window.location.search;
    exchange(m[1]).then(() => navigate(to, { replace: true })).catch(() => { setErr("Sign-in failed. Please try again."); navigate(to, { replace: true }); });
  }, [exchange, navigate]);
  return <Page testid="account-callback"><p className="py-24 text-center text-sm text-[#5E5675]">{err || "Signing you in…"}</p></Page>;
}
